interface ForestTrailInputOptions {
  minX: number;
  maxX: number;
  baselineY: number;
  speed?: number;
  depth?: number;
  onPositionChanged?: (x: number) => void;
}

export class ForestTrailInputController {
  private readonly pointerZone: Phaser.GameObjects.Zone;
  private readonly cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private readonly leftKey?: Phaser.Input.Keyboard.Key;
  private readonly rightKey?: Phaser.Input.Keyboard.Key;
  private readonly speed: number;
  private targetX: number | null = null;
  private enabled = false;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly character: Phaser.GameObjects.Image,
    private readonly options: ForestTrailInputOptions,
  ) {
    this.speed = options.speed ?? 180;
    const keyboard = scene.input.keyboard;
    this.cursors = keyboard?.createCursorKeys();
    this.leftKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.rightKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.D);

    this.pointerZone = scene.add.zone(
      (options.minX + options.maxX) * 0.5,
      options.baselineY - 30,
      options.maxX - options.minX,
      190,
    ).setDepth(options.depth ?? 40).setInteractive({ useHandCursor: true });
    this.pointerZone.input!.enabled = false;
    this.pointerZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      this.targetX = Phaser.Math.Clamp(pointer.worldX, options.minX, options.maxX);
    });

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  enable() {
    this.enabled = true;
    this.pointerZone.input!.enabled = true;
  }

  disable() {
    this.enabled = false;
    this.targetX = null;
    this.pointerZone.input!.enabled = false;
  }

  private update(_time: number, delta: number) {
    if (!this.enabled) return;
    const left = this.cursors?.left.isDown || this.leftKey?.isDown;
    const right = this.cursors?.right.isDown || this.rightKey?.isDown;
    const direction = (right ? 1 : 0) - (left ? 1 : 0);
    const step = this.speed * Math.min(delta, 32) / 1000;

    if (direction !== 0) {
      this.targetX = null;
      this.character.x = Phaser.Math.Clamp(
        this.character.x + direction * step,
        this.options.minX,
        this.options.maxX,
      );
    } else if (this.targetX !== null) {
      const distance = this.targetX - this.character.x;
      if (Math.abs(distance) <= step) {
        this.character.x = this.targetX;
        this.targetX = null;
      } else {
        this.character.x += Math.sign(distance) * step;
      }
    }

    this.character.y = this.options.baselineY;
    this.options.onPositionChanged?.(this.character.x);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.pointerZone.destroy();
  }
}
